/**
 * 통합 보상 지급 (일감 S2) — GameScene grantResolvedReward/emptyReward 추출 (Phaser-free).
 * 상점/던전/일일상점이 공용하는 단일 지급 경로 (RewardSpec → ResolvedReward → 분배).
 */
import { BigNumber } from "@yjsgames/bignumber";
import type { ResolvedReward } from "../../systems/RewardResolver.js";
import type { ModuleRarity } from "../../modules/ProjectileModule.js";
import type { ModuleInstance } from "../../models/ModuleInstance.js";
import type { SynergyReward } from "../../data/moduleSynergyDefinitions.js";
import type { GameContext } from "../GameContext.js";

export interface RewardServiceDeps {
  ctx: GameContext;
  /**
   * 모듈 지급 — 도감 기록·사운드·UI 갱신 포함 (현재 셸 grantModule 위임, S2c gridCommands로 이전 예정).
   * 반환: 생성된 인스턴스(획득 모달이 실제 조각 모양을 그리는 데 사용). 미존재 정의면 null.
   */
  grantModule: (definitionId: string, rarity?: ModuleRarity) => ModuleInstance | null;
}

export class RewardService {
  constructor(private readonly deps: RewardServiceDeps) {}

  /**
   * 해석된 보상 지급 — economy/upgradeInv/rebirth/grantModule 분배. artifacts는 미지급(유물 시스템 미구현).
   * 반환: drop 순서대로 생성된 ModuleInstance[] (획득 모달이 인스턴스별 파이프 모양을 그리는 데 사용).
   */
  grant(r: ResolvedReward): ModuleInstance[] {
    const ctx = this.deps.ctx;
    if (r.gold.gt(BigNumber.ZERO)) ctx.economy().addGold(r.gold);
    if (r.diamond.gt(BigNumber.ZERO)) ctx.economy().addDiamond(r.diamond);
    if (r.upgradeStone > 0) ctx.upgradeInv().addStone(r.upgradeStone);
    if (r.rebirthPoint > 0) ctx.rebirth().addPoint(r.rebirthPoint);
    // 추첨된 등급(drop.rarity)을 인스턴스 등급 오버라이드로 지급 — 같은 모듈이라도 등급별 효과가 적용된다.
    const instances: ModuleInstance[] = [];
    for (const drop of r.moduleDrops) {
      const inst = this.deps.grantModule(drop.id, drop.rarity);
      if (inst) instances.push(inst);
    }
    return instances;
  }

  /**
   * 시너지 최초 발견 보상 — SynergyReward(gold/diamond/stone)를 economy/upgradeInv에 즉시 지급.
   * relic은 MVP 미지급(어떤 relicId인지 데이터 미정의 — 초기 12개 시너지는 gold/diamond/stone만 사용).
   */
  grantSynergyReward(reward: SynergyReward): void {
    const ctx = this.deps.ctx;
    if (reward.gold && reward.gold > 0) ctx.economy().addGold(BigNumber.fromNumber(reward.gold));
    if (reward.diamond && reward.diamond > 0)
      ctx.economy().addDiamond(BigNumber.fromNumber(reward.diamond));
    if (reward.stone && reward.stone > 0) ctx.upgradeInv().addStone(reward.stone);
  }

  /** 빈 ResolvedReward에 일부 필드만 덮어써 grant에 전달 (상점 지급 경로 통일용). */
  static empty(over: Partial<ResolvedReward> = {}): ResolvedReward {
    return {
      gold: BigNumber.ZERO,
      diamond: BigNumber.ZERO,
      rebirthPoint: 0,
      upgradeStone: 0,
      moduleDrops: [],
      artifacts: [],
      ...over,
    };
  }
}
